import { ReactNode, createContext, useContext } from 'react'
import { z } from 'zod'
import { Form } from '$components'
import { EditTeamMemberDetailsContent } from './content'
import { EditTeamMemberDetailsControls } from './controls'

export enum Input {
  Role = 'role',
}

export const editTeamMemberDetailsSchema = z.object({
  [Input.Role]: z.object({
    id: z.string(),
    value: z.string(),
  }),
})

export type EditTeamMemberDetailsSchema = z.infer<typeof editTeamMemberDetailsSchema>

type EditTeamMemberDetailsFormContextType = {
  onSubmit: (values: EditTeamMemberDetailsSchema) => void
  onCancel: () => void
  isLoading?: boolean,
}

const EditTeamMemberDetailsFormContext = createContext<EditTeamMemberDetailsFormContextType | undefined>(undefined)

type EditTeamMemberDetailsFormProps = EditTeamMemberDetailsFormContextType & {
  children?: ReactNode,
  defaultValues?: Partial<EditTeamMemberDetailsSchema>,
}

export function EditTeamMemberDetailsForm({
  onSubmit,
  onCancel,
  isLoading = false,
  children,
  defaultValues,
}: EditTeamMemberDetailsFormProps) {

  const context: EditTeamMemberDetailsFormContextType = {
    onSubmit,
    onCancel,
    isLoading,
  }

  return (
    <EditTeamMemberDetailsFormContext.Provider value={context}>
      <Form<EditTeamMemberDetailsSchema, typeof editTeamMemberDetailsSchema>
        schema={editTeamMemberDetailsSchema}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
      >
        {children}
      </Form>
    </EditTeamMemberDetailsFormContext.Provider>
  )
}

export function useEditTeamMemberDetailsForm() {
  const context = useContext(EditTeamMemberDetailsFormContext)
  if (context === undefined) {
    throw new Error('useEditTeamMemberDetailsForm must be used within an EditTeamMemberDetailsFormProvider')
  }
  return context
}

// Compound components
EditTeamMemberDetailsForm.Content = EditTeamMemberDetailsContent
EditTeamMemberDetailsForm.Controls = EditTeamMemberDetailsControls
